import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CalendarPanelComponent } from '../calendar-panel/calendar-panel.component';

const ESTIMATED_DROPDOWN_HEIGHT = 340;

@Component({
  selector: 'app-datepicker-single',
  templateUrl: './datepicker-single.component.html',
  styleUrls: ['./datepicker-single.component.scss'],
  standalone: true,
  imports: [CommonModule, CalendarPanelComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerSingleComponent),
      multi: true,
    },
  ],
})
export class DatepickerSingleComponent implements ControlValueAccessor, OnDestroy {
  @Input() placeholder = 'dd/mm/aaaa';
  @Input() min: Date | null = null;
  @Input() max: Date | null = null;

  @HostBinding('class.app-datepicker-single') readonly hostClass = true;

  value: Date | null = null;
  isDisabled = false;
  isOpen = false;
  viewYear = new Date().getFullYear();
  viewMonth = new Date().getMonth();
  dropdownStyle: { [key: string]: string } = {};

  private onChange: (v: Date | null) => void = () => {};
  onTouched: () => void = () => {};
  private readonly onWindowScroll = () => this.close();

  constructor(private el: ElementRef, private cd: ChangeDetectorRef) {}

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onWindowScroll, true);
  }

  toggleDropdown(): void {
    if (this.isDisabled) return;
    if (this.isOpen) {
      this.close();
      return;
    }

    this.isOpen = true;
    const ref = this.value || new Date();
    this.viewYear = ref.getFullYear();
    this.viewMonth = ref.getMonth();
    this.updateDropdownPosition();
    window.addEventListener('scroll', this.onWindowScroll, true);
  }

  close(): void {
    this.isOpen = false;
    window.removeEventListener('scroll', this.onWindowScroll, true);
    this.cd.markForCheck();
  }

  private updateDropdownPosition(): void {
    const rect = this.el.nativeElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < ESTIMATED_DROPDOWN_HEIGHT && rect.top > spaceBelow;

    this.dropdownStyle = {
      position: 'fixed',
      left: `${rect.left}px`,
      width: '280px',
      ...(openUpward
        ? { bottom: `${window.innerHeight - rect.top + 4}px` }
        : { top: `${rect.bottom + 4}px` }),
    };
  }

  clear(): void {
    this.value = null;
    this.close();
    this.onChange(this.value);
    this.onTouched();
    this.cd.markForCheck();
  }

  onDateSelect(date: Date): void {
    this.value = date;
    this.close();
    this.onChange(this.value);
    this.onTouched();
    this.cd.markForCheck();
  }

  onViewMonthChange(month: number): void {
    this.viewMonth = month;
  }

  onViewYearChange(year: number): void {
    this.viewYear = year;
  }

  formatDate(date: Date): string {
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.isOpen && !this.el.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  writeValue(val: Date | null): void {
    this.value = val;
    this.cd.markForCheck();
  }

  registerOnChange(fn: (v: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cd.markForCheck();
  }
}
